// Heuristic grouping of NJ MCCS field names into human-friendly categories.
// Used by PersonDetail to render related fields together.

export type GroupKey = "identity" | "case" | "court" | "charge" | "hearing" | "other";

interface GroupDef {
  key: GroupKey;
  label: string;
  match: RegExp;
  order: number;
}

const GROUPS: GroupDef[] = [
  { key: "identity", label: "Identity", order: 1, match: /^(defendant|name|gender|eyes|hair|dob|date of birth|address|height|weight|race|alias)$/i },
  { key: "case",     label: "Case",     order: 2, match: /^(prefix\/year\/number|case|case number|case status|status|type|complaint)$/i },
  { key: "court",    label: "Court",    order: 3, match: /^(court|court name|court phone|court room|address)$/i },
  { key: "charge",   label: "Charge",   order: 4, match: /^(offense|offense description|statute)$/i },
  { key: "hearing",  label: "Hearing",  order: 5, match: /^(court date.*|hearing date|court date\/time|disposition|disposition date|warrant status)$/i },
];

const GROUP_META: Record<GroupKey, { label: string; order: number; hint?: string }> = {
  identity: { label: "Identity",    order: 1, hint: "physical & contact info" },
  case:     { label: "Case",        order: 2, hint: "case identifiers" },
  court:    { label: "Court",       order: 3, hint: "where & who" },
  charge:   { label: "Charge",      order: 4, hint: "offense details" },
  hearing:  { label: "Hearing",     order: 5, hint: "schedule & disposition" },
  other:    { label: "Other",       order: 6 },
};

export function classifyField(label: string): GroupKey {
  const trimmed = label.trim();
  for (const g of GROUPS) {
    if (g.match.test(trimmed)) return g.key;
  }
  return "other";
}

export function groupFields(
  fields: Record<string, string | string[]>,
): Array<{ key: GroupKey; label: string; hint?: string; entries: Array<[string, string | string[]]> }> {
  const groups: Record<GroupKey, Array<[string, string | string[]]>> = {
    identity: [], case: [], court: [], charge: [], hearing: [], other: [],
  };
  for (const [k, v] of Object.entries(fields)) {
    groups[classifyField(k)].push([k, v]);
  }
  return (Object.keys(groups) as GroupKey[])
    .filter((k) => groups[k].length > 0)
    .sort((a, b) => GROUP_META[a].order - GROUP_META[b].order)
    .map((k) => ({ key: k, label: GROUP_META[k].label, hint: GROUP_META[k].hint, entries: groups[k] }));
}

/** Pull a single "headline" field from a record summary if available - used as a card title. */
export function recordHeadline(fields: Record<string, string | string[]>): string {
  const offense = pickString(fields["Offense Description"]);
  const caseNo = pickString(fields["Prefix/Year/Number"] ?? fields["Case"] ?? fields["Case Number"]);
  if (offense && caseNo) return `${caseNo} · ${offense}`;
  return offense || caseNo || "Record";
}

export function recordSubtitle(fields: Record<string, string | string[]>): string {
  const court = pickString(fields["Court"]);
  const date = pickString(
    fields["Court Date/Time"] ?? fields["Court Date"] ?? fields["Offense Date"],
  );
  return [court, date].filter(Boolean).join(" · ");
}

export function recordStatus(fields: Record<string, string | string[]>): string | null {
  return pickString(fields["Status"] ?? fields["Case Status"]) || null;
}

/**
 * Classify a court-status string as outstanding (still needs attention)
 * vs not. Used by the "Outstanding court records" filter, the Open Items
 * summary, the PDF packet builder, and the client-summary generator.
 *
 * Semantics (2026-05-13): exclusion list, not inclusion list. A case is
 * outstanding UNLESS it's been Disposed or Transferred to another court.
 * Everything else passes - including statuses we used to gate out
 * (Closed-to-MVC, Recalled Warrant) and statuses we don't recognize.
 *
 *   Disposed         → NOT outstanding (case adjudicated)
 *   Transferred To   → NOT outstanding (no longer this court's responsibility)
 *   anything else    → outstanding (Time Payment, DL Suspended, Active,
 *                       Warrant, Closed to MVC, Recalled Warrant, etc.)
 *
 * Returns false for null/missing status - without data we can't claim
 * the case is outstanding.
 *
 * Note: function name kept as `isOpenStatus` for callsite continuity; the
 * concept it computes is "outstanding." The two are now synonymous in
 * this codebase.
 */
export function isOpenStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  const s = status.toLowerCase().trim();
  if (/^disposed/.test(s)) return false;
  if (/transferred?\s*to/.test(s)) return false;
  return true;
}

function pickString(v: string | string[] | undefined): string {
  if (!v) return "";
  if (Array.isArray(v)) return v[v.length - 1] ?? ""; // tend to be longer/more complete
  return v;
}
